import { PAGE_ROUTES } from "~/constants/pages";

/** Next pathname or asPath segment without trailing slash (handles `trailingSlash: true`). */
export const normalizePathname = (pathname: string | undefined): string => pathname?.replace(/\/+$/, "") || "/";

/** Path-only string from `routeChangeComplete` / `asPath` (query, hash, absolute URLs). */
export const normalizedPathFromNavigationUrl = (url: string): string => {
  const noQueryHash = url.split("?")[0].split("#")[0];
  if (noQueryHash.includes("://")) {
    try {
      const pathname = new URL(noQueryHash).pathname;
      return pathname.replace(/\/+$/, "") || "/";
    } catch {
      return noQueryHash.replace(/\/+$/, "") || "/";
    }
  }
  return noQueryHash.replace(/\/+$/, "") || "/";
};

/** Routes where the order page / useOrder owns clearing `pageLoading` (incl. basePath suffix e.g. `/buy/order`). */
export const isOrderLoaderOwnedRoute = (normalizedPathname: string): boolean =>
  normalizedPathname === PAGE_ROUTES.BILLING_SUMMARY ||
  normalizedPathname === PAGE_ROUTES.ORDER_FIBER ||
  normalizedPathname.endsWith(PAGE_ROUTES.BILLING_SUMMARY) ||
  normalizedPathname.endsWith(PAGE_ROUTES.ORDER_FIBER);

/** Whether a navigation URL resolves to /order or /order-fiber after optional basePath strip. */
export const isOrderRouteNavigationUrl = (url: string, basePath?: string): boolean => {
  let p = normalizedPathFromNavigationUrl(url);
  const base = basePath ?? "";
  if (base && p.startsWith(base)) {
    p = p.slice(base.length) || "/";
  }
  return isOrderLoaderOwnedRoute(normalizePathname(p));
};

/** Monarch loader config keys: `/order` → `_order`, `/plan-package` → `_plan-package` (not `pathname.replace("/", "_")`, which breaks with trailing slashes). */
export const monarchFormattedPageKey = (pathname: string | undefined): string => {
  const n = normalizePathname(pathname);
  if (n === "/") return "_";
  const segments = n.split("/").filter(Boolean);
  return segments.length ? `_${segments.join("_")}` : "_";
};

/** True if pathname or asPath (minus basePath) is an order route — covers static export / SPA edge cases. */
export const orderRouteMatchesForPageLoading = (opts: {
  pathname?: string;
  asPath?: string;
  basePath?: string;
}): boolean => {
  if (isOrderLoaderOwnedRoute(normalizePathname(opts.pathname))) return true;
  const raw = opts.asPath?.split("?")[0]?.split("#")[0] ?? "";
  const base = opts.basePath ?? "";
  const withoutBase = base && raw.startsWith(base) ? raw.slice(base.length) || "/" : raw;
  return isOrderLoaderOwnedRoute(normalizePathname(withoutBase));
};
